import type { SourcingEventDto } from "../../autogen/bff-api";
import {
  hasTenderStageQualification,
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../sourcing-events/buyer/wizard/utils/procedure";
import { isWithoutCompetition } from "../sourcing-events/buyer/wizard/utils/noCompetition";

// ADR-033 D1: negotiation is not a tab — the round pager scopes the tabs instead.
export type EventTab =
  | "request"
  | "qualification"
  | "minimumRequirements"
  | "dpsSuppliers"
  | "dpsCompetitions"
  | "bids"
  | "evaluation"
  | "award"
  | "communication";

export type TabGroup = "basis" | "process";

export interface TabDefinition {
  key: EventTab;
  labelKey: string;
  group: TabGroup;
}

export const GROUPS: { id: TabGroup; labelKey: string }[] = [
  { id: "basis", labelKey: "Basis" },
  { id: "process", labelKey: "Process" },
];

export const TABS: TabDefinition[] = [
  { key: "request", labelKey: "Request", group: "basis" },
  { key: "communication", labelKey: "Communication", group: "basis" },
  { key: "qualification", labelKey: "Qualification", group: "process" },
  { key: "minimumRequirements", labelKey: "Minimum requirements", group: "process" },
  { key: "dpsSuppliers", labelKey: "Suppliers", group: "process" },
  { key: "dpsCompetitions", labelKey: "Competitions", group: "process" },
  { key: "bids", labelKey: "Bids", group: "process" },
  { key: "evaluation", labelKey: "Evaluation", group: "process" },
  { key: "award", labelKey: "Award", group: "process" },
];

// A scheme admits suppliers and hosts purchases; it never collects a tender or awards anything
// itself, so the tabs that would ask it to are absent (FOA § 26-7).
const SCHEME_ONLY_TABS: EventTab[] = ["dpsSuppliers", "dpsCompetitions"];
const TABS_HIDDEN_ON_A_SCHEME: EventTab[] = [
  "qualification",
  "minimumRequirements",
  "bids",
  "evaluation",
  "award",
];

// Awarded straight to one supplier: there were no tenders to qualify or evaluate.
const TABS_HIDDEN_WITHOUT_COMPETITION: EventTab[] = ["qualification", "minimumRequirements", "evaluation"];

export const DEFAULT_TAB: EventTab = "request";

export const parseEventTab = (value: string | null): EventTab | null =>
  TABS.find((tab) => tab.key === value)?.key ?? null;

const procedureTypeOf = (event: SourcingEventDto) =>
  event.eventContext.type === "Public" ? event.eventContext.procedureType : undefined;

const isVisible = (tab: TabDefinition, event: SourcingEventDto): boolean => {
  const procedureType = procedureTypeOf(event);
  const scheme = isSchemeProcedure(procedureType);
  if (tab.key === "dpsCompetitions") return scheme;
  if (SCHEME_ONLY_TABS.includes(tab.key)) return scheme;
  if (scheme) return !TABS_HIDDEN_ON_A_SCHEME.includes(tab.key);
  if (isWithoutCompetition(procedureType) && TABS_HIDDEN_WITHOUT_COMPETITION.includes(tab.key)) return false;
  // Public procurements always qualify their suppliers — two-stage ones on the requests,
  // single-stage ones on the tenders. Private competitions have no qualification phase.
  if (tab.key === "qualification") {
    return isTwoStageProcedure(procedureType) || hasTenderStageQualification(procedureType);
  }
  if (tab.key === "minimumRequirements") {
    return (event.eventData.minimumRequirements ?? []).length > 0;
  }
  return true;
};

export interface VisibleTabGroup {
  id: TabGroup;
  labelKey: string;
  tabs: TabDefinition[];
}

export const visibleTabGroups = (event: SourcingEventDto): VisibleTabGroup[] =>
  GROUPS.map((group) => ({
    ...group,
    tabs: TABS.filter((tab) => tab.group === group.id && isVisible(tab, event)),
  })).filter((group) => group.tabs.length > 0);

// Chakra indexes a tab by document position, not by its place among TabList's direct children,
// so the flat list driving `index` and the panel order is built from the groups as they render.
export const visibleEventTabs = (event: SourcingEventDto): TabDefinition[] =>
  visibleTabGroups(event).flatMap((group) => group.tabs);
